/**
 * 菜单过滤工具函数
 *
 * 基于用户权限过滤菜单
 */

import type { UserPermission } from '@/types/permission';
import {
  MENU_PERMISSIONS,
  PUBLIC_MENU_KEYS,
  ROLE_BLOCKED_MENU_KEYS,
} from '@/config/menu-permissions';
import { PERMISSION_MAPPING } from '@/config/permission-mapping';
import { createLogger } from './logger';

const logger = createLogger('MenuFilter');

/**
 * 菜单项接口
 */
export interface MenuItem {
  key: string;
  name: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}

const ROLE_PRIORITY = ['admin', 'director', 'department_head', 'hrbp', 'employee'] as const;

function resolvePrimaryRole(roleCodes: string[] = []): string | null {
  for (const role of ROLE_PRIORITY) {
    if (roleCodes.includes(role)) {
      return role;
    }
  }
  return roleCodes[0] || null;
}

/**
 * 检查用户是否有权限访问菜单
 *
 * @param menuKey - 菜单的 key
 * @param userPermission - 用户权限对象（Arco Pro 格式）
 * @returns 是否有权限
 */
export function hasMenuPermission(
  menuKey: string,
  userPermission: UserPermission,
  roleCodes: string[] = []
): boolean {
  const primaryRole = resolvePrimaryRole(roleCodes);
  if (primaryRole && (ROLE_BLOCKED_MENU_KEYS[primaryRole] || []).includes(menuKey)) {
    return false;
  }

  // 公开菜单，所有人可见
  if (PUBLIC_MENU_KEYS.includes(menuKey)) {
    return true;
  }

  // 未配置权限要求的菜单，默认不可见（避免漏配导致越权展示）
  const requiredPermissions = MENU_PERMISSIONS[menuKey];
  if (!requiredPermissions || requiredPermissions.length === 0) {
    logger.warn(`菜单 ${menuKey} 未配置权限，默认隐藏`);
    return false;
  }

  logger.debug(`检查菜单 ${menuKey}:`, {
    requiredPermissions,
    userPermission,
  });

  // 检查用户是否有任意一个所需权限
  const result = requiredPermissions.some((permissionCode) => {
    // 使用 PERMISSION_MAPPING 转换权限代码
    const mapping = PERMISSION_MAPPING[permissionCode];
    if (!mapping) {
      // 如果权限代码未在映射表中，则拒绝访问
      logger.warn(`未找到权限代码映射: ${permissionCode}`);
      return false;
    }

    const { resource, action } = mapping;
    // 不标准化 action，直接使用原始名称
    // 与 transformPermissions 保持一致（不标准化）

    // 检查用户是否有该资源的该操作权限
    const userActions = userPermission[resource];
    const hasPermission = userActions && userActions.includes(action);

    logger.debug('权限检查:', {
      permissionCode,
      resource,
      action,
      userActions,
      hasPermission,
    });

    return hasPermission;
  });

  logger.debug(`菜单 ${menuKey} 权限检查结果: ${result}`);
  return result;
}

/**
 * 过滤菜单列表（递归）
 *
 * @param menus - 菜单列表
 * @param userPermission - 用户权限对象（Arco Pro 格式）
 * @returns 过滤后的菜单列表
 */
export function filterMenus(
  menus: MenuItem[],
  userPermission: UserPermission,
  roleCodes: string[] = []
): MenuItem[] {
  return menus
    .filter((menu) => hasMenuPermission(menu.key, userPermission, roleCodes))
    .map((menu) => ({
      ...menu,
      // 递归过滤子菜单
      children: menu.children
        ? filterMenus(menu.children, userPermission, roleCodes)
        : undefined,
    }))
    .filter((menu) => {
      // 如果父菜单的所有子菜单都被过滤掉，也隐藏父菜单
      if (!menu.children || menu.children.length === 0) {
        return true; // 没有子菜单或子菜单被全部过滤，保留父菜单
      }
      return true; // 有子菜单，保留父菜单
    });
}

/**
 * 检查用户是否有权限访问路由
 *
 * @param routeKey - 路由的 key
 * @param userPermission - 用户权限对象
 * @returns 是否有权限
 */
export function hasRoutePermission(
  routeKey: string,
  userPermission: UserPermission,
  roleCodes: string[] = []
): boolean {
  // 路由权限与菜单权限共用一套配置
  return hasMenuPermission(routeKey, userPermission, roleCodes);
}
